import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/stores/auth';
import { useNavigate } from '@tanstack/react-router';
import { rememberInactivityProfile } from '@/helpers/inactivityProfile';

const EVENTS = ['mousedown', 'keypress', 'scroll', 'touchstart', 'click'];

const TIMEOUT = 30 * 60 * 1000; // 30 minutes
const WARNING_TIME = 5 * 60 * 1000; // 5 minutes before timeout
const WARNING_TIMEOUT = TIMEOUT - WARNING_TIME; // Show warning at 25 minutes

const LAST_ACTIVITY_KEY = 'last_activity_timestamp';
// Cross-tab gate. While set, `_layout.beforeLoad` redirects any attempt to
// access a protected route back to the login page. Cleared by the login
// page when the user clicks "Logga in", which marks their explicit intent
// to re-authenticate before resuming.
//
// This gate is tamperable from DevTools (a user who knows the key can
// remove it). True tamper resistance is not achievable purely client-side
// while keeping the Ory session alive. See the comment on the gate check
// in `src/routes/$tenantId/_layout.tsx` for more.
export const INACTIVITY_LOGOUT_KEY = 'inactivity_logout';

export const useInactivityTimeout = () => {
  const { logout, userProfile } = useAuth();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const performLogout = useCallback(() => {
    if (userProfile) {
      const tenantId = userProfile.tenant_id;
      const { pathname, search, hash } = window.location;
      const { previousUserProfile, preImpersonationLocation } =
        useAuth.getState();
      rememberInactivityProfile(
        userProfile.expires_at && previousUserProfile
          ? {
              profileId: userProfile.id,
              previousProfileId: previousUserProfile.id,
              preImpersonationLocation: preImpersonationLocation ?? undefined,
            }
          : { profileId: userProfile.id },
      );
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      // Clear local app state only. Intentionally do NOT destroy the Ory
      // session via createBrowserLogoutFlow: we want the user to be able
      // to log back in within the Ory session lifespan without having to
      // re-enter their password and verification code.
      logout();
      localStorage.setItem(INACTIVITY_LOGOUT_KEY, '1');
      navigate({
        to: '/$tenantId/login',
        replace: true,
        params: { tenantId },
        search: { returnTo: `${pathname}${search}${hash}` },
      });
    }
  }, [userProfile, logout, navigate]);

  const checkInactivityTimeout = useCallback(() => {
    if (!userProfile) {
      return false;
    }

    const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!lastActivityStr) {
      return false;
    }

    const lastActivity = parseInt(lastActivityStr, 10);
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivity;

    if (timeSinceLastActivity >= TIMEOUT) {
      return true;
    }

    return false;
  }, [userProfile]);

  const resetTimeout = useCallback(() => {
    if (!userProfile) {
      return;
    }

    const now = Date.now();
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }

    setShowWarning(false);

    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      setRemainingSeconds(WARNING_TIME / 1000);
    }, WARNING_TIMEOUT);

    timeoutRef.current = setTimeout(() => {
      performLogout();
    }, TIMEOUT);
  }, [userProfile, performLogout]);

  const handleActivity = useCallback(() => {
    if (!userProfile) {
      return;
    }
    resetTimeout();
  }, [userProfile, resetTimeout]);

  useEffect(() => {
    if (!userProfile) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = null;
      }
      setShowWarning(false);
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      return;
    }

    if (checkInactivityTimeout()) {
      performLogout();
      return;
    }

    resetTimeout();

    EVENTS.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible - check if timeout has elapsed
        if (checkInactivityTimeout()) {
          performLogout();
        } else {
          // Check how much time has passed and set appropriate timeout
          const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_KEY);
          if (lastActivityStr) {
            const lastActivity = parseInt(lastActivityStr, 10);
            const now = Date.now();
            const timeSinceLastActivity = now - lastActivity;
            const remainingTime = TIMEOUT - timeSinceLastActivity;
            const warningRemainingTime =
              WARNING_TIMEOUT - timeSinceLastActivity;

            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
            }
            if (warningTimeoutRef.current) {
              clearTimeout(warningTimeoutRef.current);
            }

            if (warningRemainingTime <= 0) {
              setShowWarning(true);
              setRemainingSeconds(Math.floor(remainingTime / 1000));
              timeoutRef.current = setTimeout(() => {
                performLogout();
              }, remainingTime);
            } else {
              setShowWarning(false);
              warningTimeoutRef.current = setTimeout(() => {
                setShowWarning(true);
                setRemainingSeconds(WARNING_TIME / 1000);
              }, warningRemainingTime);

              timeoutRef.current = setTimeout(() => {
                performLogout();
              }, remainingTime);
            }
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }

      EVENTS.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });

      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    userProfile,
    handleActivity,
    resetTimeout,
    checkInactivityTimeout,
    performLogout,
  ]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, []);

  return {
    resetTimeout: handleActivity,
    showWarning,
    remainingSeconds,
  };
};
